import React, { useEffect, useState } from "react";
import { FaAward, FaRegEdit, FaDownload, FaTimes } from "react-icons/fa";
import { GiAchievement } from "react-icons/gi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import axios from "axios";
import DeleteClientModal from "../../Modals/DeleteClientModal";
import AddCertificates from "../../Modals/AddCertificatesModal";

// Image Modal Component for Zoom View
const ImageModal = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentIndex, images.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(images[currentIndex]);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `certificate-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full p-6 flex flex-col items-center max-h-[95vh] overflow-hidden">
        {/* Header with title and controls */}
        <div className="w-full flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Certificate Image{" "}
            {images.length > 1 && `(${currentIndex + 1} of ${images.length})`}
          </h3>

          <div className="flex gap-2">
            <button
              onClick={downloadImage}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
              title="Download Image"
            >
              <FaDownload size={16} />
              <span className="hidden sm:inline">Download</span>
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              title="Close (ESC)"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Image Container */}
        <div className="relative w-full flex-1 flex items-center justify-center min-h-0">
          <img
            src={images[currentIndex]}
            alt={`Certificate ${currentIndex + 1}`}
            className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-lg"
          />

          {/* Navigation Arrows for multiple images */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full shadow-xl hover:bg-opacity-100 hover:scale-110 transition-all duration-200 backdrop-blur-sm"
                title="Previous image (←)"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full shadow-xl hover:bg-opacity-100 hover:scale-110 transition-all duration-200 backdrop-blur-sm"
                title="Next image (→)"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Bottom Controls */}
        <div className="w-full mt-4 pt-3 border-t border-gray-200">
          {/* Dots Navigation for multiple images */}
          {images.length > 1 && (
            <div className="flex justify-center gap-3 mb-3">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 transform hover:scale-150 ${
                    index === currentIndex
                      ? "bg-blue-600 scale-125"
                      : "bg-gray-400 hover:bg-gray-600"
                  }`}
                  title={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Keyboard shortcuts info */}
          <div className="flex justify-center">
            <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
              <span className="hidden sm:inline">
                Use ← → arrow keys to navigate • Press ESC to close
              </span>
              <span className="sm:hidden">ESC to close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Image Slider Component
const ImageSlider = ({
  images,
  autoSlide = true,
  slideInterval = 4000,
  onImageClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto slide effect - pause on hover
  useEffect(() => {
    if (!autoSlide || !images || images.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, slideInterval);

    return () => clearInterval(interval);
  }, [images, autoSlide, slideInterval, isPaused]);

  // Handle manual navigation
  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  };

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-20 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
        <div className="text-center">
          <FaAward className="w-6 h-6 text-gray-400 mx-auto mb-1" />
          <span className="text-xs text-gray-500">No Image</span>
        </div>
      </div>
    );
  }

  if (images.length === 1) {
    return (
      <div
        className="relative w-full h-20 rounded-lg overflow-hidden border-2 border-gray-200 group cursor-pointer hover:border-blue-400 transition-all duration-300 hover:shadow-md"
        onClick={() => {
          console.log(
            "DEBUG: Single image clicked, calling onImageClick with index 0"
          );
          onImageClick && onImageClick(0);
        }}
      >
        <img
          src={images[0]}
          alt="Certificate"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <div className="bg-white bg-opacity-20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
            <ZoomIn className="text-white drop-shadow-lg" size={16} />
          </div>
        </div>
        {/* <div className="absolute top-1 right-1 bg-black bg-opacity-40 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          Click to zoom
        </div> */}
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-20 rounded-lg overflow-hidden border-2 border-gray-200 group cursor-pointer hover:border-blue-400 transition-all duration-300 hover:shadow-md"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onClick={() => {
        console.log(
          "DEBUG: Multiple images clicked, calling onImageClick with index:",
          currentIndex
        );
        onImageClick && onImageClick(currentIndex);
      }}
    >
      {/* Main Image */}
      <img
        src={images[currentIndex]}
        alt={`Certificate ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />

      {/* Hover Overlay with Zoom Icon */}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
        <div className="bg-white bg-opacity-20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
          <ZoomIn className="text-white drop-shadow-lg" size={16} />
        </div>
      </div>

      {/* Click indicator */}
      {/* <div className="absolute top-1 right-1 bg-black bg-opacity-40 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        Click to zoom
      </div> */}

      {/* Navigation Arrows - Enhanced visibility and hover effects */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          goToPrevious();
        }}
        className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 text-gray-800 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-opacity-100 hover:scale-110 shadow-lg z-10"
        title="Previous image"
      >
        <ChevronLeft size={14} />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          goToNext();
        }}
        className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 text-gray-800 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-opacity-100 hover:scale-110 shadow-lg z-10"
        title="Next image"
      >
        <ChevronRight size={14} />
      </button>

      {/* Enhanced Dots Indicator */}
      <div className="absolute bottom-1.5 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(index);
            }}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 transform hover:scale-125 ${
              index === currentIndex
                ? "bg-white shadow-lg scale-110"
                : "bg-white bg-opacity-60 hover:bg-opacity-90"
            }`}
            title={`Go to image ${index + 1}`}
          />
        ))}
      </div>

      {/* Pause indicator when hovered */}
      {/* {isPaused && images.length > 1 && (
        <div className="absolute top-1 left-1 bg-black bg-opacity-40 text-white text-xs px-1.5 py-0.5 rounded">
          Paused
        </div>
      )} */}
    </div>
  );
};

const ISOCertificate = ({
  page,
  itemsPerPage,
  query,
  onDataChange,
  newCertificate,
}) => {
  const [certificates, setCertificates] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      console.log("DEBUG: Fetching ISO certificates...");

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/certificate/get-certificates`
      );

      console.log("DEBUG: API Response:", res.data);

      // Filter certificates that have ISO certificate types or names
      const isoCerts = res?.data?.filter((item) => {
        const hasISOType = item?.certificateType?.includes("ISO Certificate");
        const hasISOName = item?.certificateName?.includes("ISO");
        console.log(
          `DEBUG: Item ${item.name} - hasISOType: ${hasISOType}, hasISOName: ${hasISOName}`
        );
        return hasISOType || hasISOName;
      });

      console.log("DEBUG: Filtered ISO certificates:", isoCerts);

      setCertificates(isoCerts || []);
      setSuccess("Certificates fetched successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("DEBUG: Fetch error:", err);
      setError("Failed to fetch certificates");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // Handle new certificate by refetching data
  useEffect(() => {
    if (newCertificate && newCertificate.name) {
      console.log("DEBUG: New certificate received:", newCertificate);
      console.log("DEBUG: Refetching data to ensure consistency");
      fetchCertificates();
    }
  }, [newCertificate]);

  // Filter and paginate
  useEffect(() => {
    console.log(
      "DEBUG: Pagination effect - certificates:",
      certificates.length,
      "items"
    );
    console.log("DEBUG: Current query:", query);

    const filtered = certificates.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );

    console.log(
      "DEBUG: Filtered certificates by query:",
      filtered.length,
      "items"
    );

    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const paginated = filtered.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );

    console.log(
      "DEBUG: Paginated data:",
      paginated.length,
      "items for page",
      page
    );

    setPaginatedData(paginated);
    onDataChange(totalPages, totalItems);
  }, [certificates, query, page, itemsPerPage]);

  const handleImageClick = (images, index) => {
    console.log("DEBUG: Image clicked with images:", images, "index:", index);
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedCertificate?._id) return;

    try {
      console.log("DEBUG: Deleting certificate:", selectedCertificate._id);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/certificate/delete-certificate/${selectedCertificate._id}`
      );

      setCertificates((prev) =>
        prev.filter((item) => item._id !== selectedCertificate._id)
      );

      console.log("DEBUG: Certificate deleted successfully");
      setSuccess("Certificate deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("DEBUG: Delete failed:", err);
      setError("Failed to delete certificate");
      setTimeout(() => setError(""), 3000);
    } finally {
      closeModals();
    }
  };

  const handleEditSubmit = async (updatedCertificate) => {
    if (!selectedCertificate?._id) return;

    try {
      console.log("DEBUG: Updating certificate:", selectedCertificate._id);
      console.log("DEBUG: Updated data:", updatedCertificate);

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/certificate/update-certificate/${selectedCertificate._id}`,
        updatedCertificate
      );

      const updatedData = res?.data;
      console.log("DEBUG: Update response:", updatedData);

      setCertificates((prev) =>
        prev.map((item) =>
          item._id === selectedCertificate._id ? updatedData : item
        )
      );

      setSuccess("Certificate updated successfully");
      setTimeout(() => setSuccess(""), 3000);
      closeModals();
    } catch (err) {
      console.error("DEBUG: Update failed:", err);
      setError("Failed to update certificate");
      setTimeout(() => setError(""), 3000);
    }
  };

  const closeModals = () => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setShowImageModal(false);
    setSelectedCertificate(null);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };

  return (
    <div className="px-2 mb-10 relative">
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded shadow z-50">
          {success}
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 text-red-800 px-4 py-2 rounded shadow z-50">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}

      {!loading && paginatedData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {paginatedData.map((item) => (
            <div key={item._id} className="col-span-1">
              <div className="relative flex flex-col md:flex-row gap-4 p-4 border-2 rounded-md bg-white shadow-sm hover:shadow-md transition">
                <div className="absolute top-4 right-4 flex gap-3 z-10">
                  <button
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => {
                      console.log("DEBUG: Edit clicked for:", item);
                      setSelectedCertificate(item);
                      setShowEditModal(true);
                    }}
                    aria-label="Edit"
                  >
                    <FaRegEdit className="h-5 w-5" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800"
                    onClick={() => {
                      console.log("DEBUG: Delete clicked for:", item);
                      setSelectedCertificate(item);
                      setShowDeleteModal(true);
                    }}
                    aria-label="Delete"
                  >
                    <RiDeleteBin6Line className="h-5 w-5" />
                  </button>
                </div>

                <div className="hidden md:flex items-center w-[20%]">
                  <ImageSlider
                    images={item.certificateImage}
                    autoSlide={true}
                    slideInterval={4000}
                    onImageClick={(index) =>
                      handleImageClick(item.certificateImage, index)
                    }
                  />
                </div>

                <div className="w-full md:w-[80%] flex flex-col gap-2">
                  <h1 className="font-semibold text-lg text-gray-800">
                    {item.name}
                  </h1>

                  {/* Display Certificate Type */}
                  <div className="flex items-center gap-2 text-sm text-green-700 font-semibold">
                    <GiAchievement size={16} />
                    {item.certificateType}
                  </div>

                  {/* Display Certificate Names if available */}
                  {item.certificateName && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.certificateName
                        .split(",")
                        .map((certName, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                          >
                            {certName.trim()}
                          </span>
                        ))}
                    </div>
                  )}

                  <p className="text-gray-600 text-sm text-justify">
                    {item.description}
                  </p>
                </div>
              </div>
              <hr />
            </div>
          ))}
        </div>
      ) : (
        !loading && (
          <div className="text-center text-gray-500 py-12">
            No ISO Certificates found.
            {certificates.length > 0 && (
              <div className="mt-2 text-sm">
                Total certificates in database: {certificates.length}
              </div>
            )}
          </div>
        )
      )}

      {/* Image Modal */}
      {showImageModal && (
        <ImageModal
          images={selectedImages}
          initialIndex={selectedImageIndex}
          onClose={closeModals}
        />
      )}

      {showDeleteModal && selectedCertificate && (
        <DeleteClientModal
          itemName={selectedCertificate.name}
          onClose={closeModals}
          onConfirm={confirmDelete}
        />
      )}

      {showEditModal && selectedCertificate && (
        <AddCertificates
          existingData={selectedCertificate}
          onClose={closeModals}
          onSubmit={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default ISOCertificate;
